var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { CustomElement, P, CustomElementUtils, CustomEventUtils, Utils, UI, avoidHandler } from '@pacem/pacem-core';
import { Matrix2D } from '@pacem/pacem-foundation';
import { UiElement, ShapeElement } from '../types';
import { TAG_MIDDLE_NAME } from '../constants';
import { Pacem2DAdapterElement } from '../adapter';
import { getStageOptions } from '../stage';
import { AdapterUtils } from './utils';
import { PacemImageElement } from '../image';
import { PacemTextElement } from '../text';
import { isGroup, isShape, isText, isImage, isUiObject, isPresentationObject, isDrawable, isLinearGradient, isRadialGradient } from '../drawing';
//namespace Pacem.Components.Drawing {
const SVG_NS = 'http://www.w3.org/2000/svg', DRAWABLE_VAR = 'pacem:2-svg-drawable', STAGE_VAR = 'pacem:2d-svg-stage', GETVAR = CustomElementUtils.getAttachedPropertyValue, SETVAR = CustomElementUtils.setAttachedPropertyValue, DELVAR = CustomElementUtils.deleteAttachedPropertyValue;
function fallback(v, f) {
    return Utils.isNull(v) ? f : v;
}
function replaceChildAt(parent, newChild, targetIndex) {
    if (targetIndex >= parent.children.length) {
        parent.appendChild(newChild);
        return null;
    }
    else {
        return parent.replaceChild(newChild, parent.children.item(targetIndex));
    }
}
let PacemSvgAdapterElement = class PacemSvgAdapterElement extends Pacem2DAdapterElement {
    constructor() {
        super(...arguments);
        this._hitTarget = null;
        this._dragInitHandler = (evt) => {
            const args = evt.detail, el = args.element, drawable = GETVAR(el, DRAWABLE_VAR), stageTransformMatrix = drawable.stage.transformMatrix, initialMatrix = drawable.transformMatrix ?? Matrix2D.identity;
            args.data = {
                stageTransformMatrix, item: drawable,
                initialTransformMatrix: initialMatrix
            };
            const reject = AdapterUtils.itemDispatch(drawable, evt, { x: 0, y: 0 });
            if (reject) {
                // reject dragging
                evt.preventDefault();
            }
        };
        this._draggingHandler = (evt) => {
            avoidHandler(evt);
            this.#dragging = true;
            const el = evt.detail.element;
            const data = evt.detail.data;
            const args = evt.detail;
            const screenOffset = { x: (args.currentPosition.x - args.origin.x) * data.stageTransformMatrix.a, y: (args.currentPosition.y - args.origin.y) * data.stageTransformMatrix.d };
            const offset = {
                x: screenOffset.x + data.initialTransformMatrix.e,
                y: screenOffset.y + data.initialTransformMatrix.f
            }, 
            // this is not correct when item or any of its parents have been rotated!
            stageOffset = {
                x: screenOffset.x + data.stageTransformMatrix.e,
                y: screenOffset.y + data.stageTransformMatrix.f
            };
            //console.log(`current pos: ${args.currentPosition.x},${args.currentPosition.y}`);
            //console.log(`origin pos: ${args.origin.x},${args.origin.y}`);
            //console.log(`offset: ${offset.x},${offset.y}`);
            //console.log(data.stageTransformMatrix);
            //console.log(el.style.transform); console.log(data.initialTransformMatrix);
            const rejected = AdapterUtils.itemDispatch(data.item, evt, stageOffset);
            if (!rejected) {
                const init = data.initialTransformMatrix;
                el.style.transform = `matrix(${init.a},${init.b},${init.c},${init.d},${offset.x},${offset.y})`;
            }
        };
        this._dragEndHandler = (evt) => {
            this.#dragging = false;
            const args = evt.detail, el = args.element, data = args.data, transform = Utils.deserializeTransform(el.style);
            // store transform
            const offset = { x: transform.e, y: transform.f };
            if (data.item instanceof UiElement) {
                data.item.translateX = offset.x;
                data.item.translateY = offset.y;
            }
            else {
                const init = data.initialTransformMatrix, actual = { a: init.a, b: init.b, c: init.c, d: init.d, e: offset.x, f: offset.y };
                Utils.extend(data.item, { transformMatrix: actual });
            }
            el.style.transform = '';
            AdapterUtils.itemDispatch(data.item, evt, offset);
        };
        this._mouseleaveHandler = (evt) => {
            if (this.#dragging) {
                return;
            }
            const hitTarget = this._hitTarget;
            if (Utils.isNull(hitTarget)) {
                return;
            }
            this._hitTarget = null;
            if (!Utils.isNull(hitTarget)) {
                this.#dragger.unregister(this._items.get(hitTarget));
                AdapterUtils.itemDispatch(hitTarget, 'out', evt);
            }
        };
        this._mousemoveHandler = (evt) => {
            var pt = CustomEventUtils.getEventCoordinates(evt).client;
            if (!this.#dragging) {
                // not dragging around
                var d = null;
                const root = evt.target.getRootNode();
                root.elementsFromPoint(pt.x, pt.y).find(i => {
                    d = GETVAR(i, DRAWABLE_VAR);
                    return d && !d.inert;
                });
                var old = this._hitTarget, val = d;
                if (Utils.isNull(d && d.stage) || !this._scenes.has(d.stage) || d.inert) {
                    val = null;
                }
                const hitTarget = this._hitTarget = val;
                if (val !== old) {
                    if (!Utils.isNull(old)) {
                        this.#dragger.unregister(this._items.get(old));
                        AdapterUtils.itemDispatch(old, 'out', evt);
                    }
                    if (!Utils.isNull(val)) {
                        if (val.draggable) {
                            this.#dragger.register(this._items.get(val));
                        }
                        AdapterUtils.itemDispatch(val, 'over', evt);
                    }
                }
                if (Utils.isNull(hitTarget)) {
                    // dispatch only if no hit target
                    const svg = evt.currentTarget;
                    const stage = GETVAR(svg, STAGE_VAR);
                    if (!Utils.isNull(stage)) {
                        AdapterUtils.stageDispatch(stage, 'move', evt);
                    }
                }
            }
        };
        this._mouseDownUpHandler = (evt) => {
            const svg = evt.currentTarget;
            if (svg instanceof SVGSVGElement) {
                const stage = GETVAR(svg, STAGE_VAR), hitTarget = this._hitTarget;
                const type = evt.type.replace(/^mouse/, '');
                const opts = getStageOptions(stage);
                if (CustomEventUtils.matchModifiers(evt, opts.clickModifiers)) {
                    if (!Utils.isNull(hitTarget) && hitTarget.stage === stage) {
                        AdapterUtils.itemDispatch(hitTarget, type, evt);
                    }
                    else {
                        AdapterUtils.stageDispatch(stage, type, evt);
                    }
                }
            }
        };
        this._scenes = new WeakMap();
        this._markers = new WeakMap();
        this._gradients = new WeakMap();
        this._items = new WeakMap();
    }
    snapshot(stage, background, type, quality) {
        if (this._scenes.has(stage)) {
            const svg = this._scenes.get(stage);
            return this.snapshotElement(svg, background, type, quality);
        }
        else {
            return Promise.resolve(null);
        }
    }
    getTransformMatrix(scene) {
        const scenes = this._scenes;
        if (scenes.has(scene)) {
            return scenes.get(scene).getScreenCTM().inverse();
        }
        return Matrix2D.identity;
    }
    #dragger;
    viewActivatedCallback() {
        super.viewActivatedCallback();
        const dragDrop = this.#dragger = document.createElement(P + '-drag-drop');
        dragDrop.mode = UI.DragDataMode.Self;
        // append
        const shell = CustomElementUtils.findAncestorShell(this);
        shell.appendChild(dragDrop);
        dragDrop.addEventListener(UI.DragDropEventType.Init, this._dragInitHandler, false);
        dragDrop.addEventListener(UI.DragDropEventType.Drag, this._draggingHandler, false);
        dragDrop.addEventListener(UI.DragDropEventType.End, this._dragEndHandler, false);
    }
    disconnectedCallback() {
        const dragger = this.#dragger;
        if (!Utils.isNull(dragger)) {
            dragger.removeEventListener(UI.DragDropEventType.Init, this._dragInitHandler, false);
            dragger.removeEventListener(UI.DragDropEventType.Drag, this._draggingHandler, false);
            dragger.removeEventListener(UI.DragDropEventType.End, this._dragEndHandler, false);
            dragger.remove();
        }
        super.disconnectedCallback();
    }
    invalidateSize(scene, size) {
        const scenes = this._scenes;
        if (!Utils.isNull(scene) && !Utils.isNullOrEmpty(size)) {
            if (scenes.has(scene)) {
                var svg = scenes.get(scene);
                svg.setAttribute('width', size.width + '');
                svg.setAttribute('height', size.height + '');
                const rect = scene.viewbox, aspectRatio = scene.aspectRatio;
                if (AdapterUtils.isValidViewbox(rect)) {
                    svg.setAttribute('viewBox', `${rect.x} ${rect.y} ${rect.width} ${rect.height}`);
                }
                else {
                    svg.removeAttribute('viewBox');
                }
                if (Utils.isNullOrEmpty(aspectRatio) || typeof aspectRatio === 'string') {
                    svg.removeAttribute('preserveAspectRatio');
                }
                else {
                    svg.setAttribute('preserveAspectRatio', `xM${aspectRatio.x.substring(1)}YM${aspectRatio.y.substring(1)} ${(aspectRatio.slice ? 'slice' : 'meet')}`);
                }
            }
        }
    }
    initialize(scene) {
        if (Utils.isNull(scene)) {
            throw 'Provided scene is null or undefined.';
        }
        const scenes = this._scenes;
        // already in the store?
        if (scenes.has(scene)) {
            return scenes.get(scene);
        }
        const stage = scene.stage;
        // empty stage DOM and clear dictionary
        stage.innerHTML = '';
        this._items = new WeakMap();
        var svg = document.createElementNS(SVG_NS, 'svg');
        svg.setAttribute('part', 'stage');
        SETVAR(svg, STAGE_VAR, scene);
        stage.appendChild(svg);
        scenes.set(scene, svg);
        const options = { passive: false, capture: true };
        svg.addEventListener('mousemove', this._mousemoveHandler, false);
        svg.addEventListener('click', this._mouseDownUpHandler, false);
        svg.addEventListener('mousedown', this._mouseDownUpHandler, false);
        svg.addEventListener('mouseup', this._mouseDownUpHandler, false);
        svg.addEventListener('mouseleave', this._mouseleaveHandler, false);
        // draw right away
        // this.draw(scene);
        return svg;
    }
    dispose(scene) {
        const scenes = this._scenes;
        if (scenes.has(scene)) {
            var svg = scenes.get(scene);
            svg.removeEventListener('mousemove', this._mousemoveHandler);
            svg.removeEventListener('click', this._mouseDownUpHandler);
            svg.removeEventListener('mousedown', this._mouseDownUpHandler);
            svg.removeEventListener('mouseup', this._mouseDownUpHandler);
            svg.removeEventListener('mouseleave', this._mouseleaveHandler);
            // remove
            DELVAR(svg, STAGE_VAR);
            svg.remove();
            scenes.delete(scene);
        }
    }
    getHitTarget(scene) {
        return this._hitTarget;
    }
    draw(scene, item, deepRedraw = false) {
        const scenes = this._scenes, dict = this._items;
        if (!Utils.isNull(scene)) {
            if (scene.adapter === this) {
                if (!scenes.has(scene)) {
                    // forgiving behavior (call initialize() if it's the case)
                    this.initialize(scene);
                }
            }
            else {
                if (scenes.has(scene)) {
                    scenes.delete(scene);
                }
                return;
            }
        }
        else {
            return;
        }
        var items = scene.datasource, flow = true, parent = scenes.get(scene);
        // item provided?
        if (!Utils.isNull(item) && dict.has(item)) {
            items = [item];
            parent = dict.get(item).parentNode;
            flow = false;
        }
        if (flow) {
            // sweep everything, quick and dirty
            parent.innerHTML = '<defs></defs>';
            this._markers = new WeakMap();
        }
        this._draw(scene, parent, items || [], flow, deepRedraw);
    }
    #dragging;
    _hasItems(object) {
        return isGroup(object);
    }
    _disposeSvg(el) {
        if (!Utils.isNull(el)) {
            var drawable = GETVAR(el, DRAWABLE_VAR);
            DELVAR(el, DRAWABLE_VAR);
            this._items.delete(drawable);
        }
    }
    _draw(scene, parent, items, flow, deepRedraw) {
        const dict = this._items;
        // children counter
        let j = 0;
        if (parent.firstElementChild instanceof SVGDefsElement) {
            j++;
        }
        if (!Utils.isNullOrEmpty(items)) {
            for (let item of items) {
                item.stage ??= scene;
                let el;
                if (!dict.has(item)) {
                    el = this._buildSVGElement(item);
                    SETVAR(el, DRAWABLE_VAR, item);
                    this._disposeSvg(replaceChildAt(parent, el, j));
                    dict.set(item, el);
                }
                else {
                    el = dict.get(item);
                    if (el.parentNode !== parent) {
                        this._disposeSvg(replaceChildAt(parent, el, j));
                    }
                }
                if (isDrawable(item)) {
                    if (item.hide) {
                        el.setAttribute('display', 'none');
                    }
                    else {
                        el.removeAttribute('display');
                    }
                    el.style.transform = '';
                }
                if (isShape(item)) {
                    const path = el;
                    path.setAttribute('d', fallback(item.pathData, 'M0,0'));
                    // markers
                    for (let { marker, suffix } of [{ marker: item.markerStart, suffix: 'start' }, { marker: item.markerEnd, suffix: 'end' }, { marker: item.markerMid, suffix: 'mid' }]) {
                        if (marker) {
                            const markerSvg = this._ensureMarker(parent, marker);
                            path.setAttribute('marker-' + suffix, `url(#${markerSvg.id})`);
                        }
                        else {
                            path.removeAttribute('marker-' + suffix);
                        }
                    }
                }
                else if (isText(item)) {
                    const text = el;
                    text.textContent = item.text;
                    text.style.fill = Utils.isNullOrEmpty(item.color) ? '' : item.color;
                    text.style.fontFamily = Utils.isNullOrEmpty(item.fontFamily) ? '' : item.fontFamily;
                    //if (item.fontSize > 0) {
                    //    text.setAttribute('font-size', item.fontSize.toString());
                    //} else {
                    //    text.removeAttribute('font-size');
                    //}
                    text.style.fontSize = Utils.isNull(item.fontSize) ? '' : item.fontSize + 'px';
                    if (!Utils.isNullOrEmpty(item.fontWeight)) {
                        text.style.fontWeight = item.fontWeight;
                    }
                    if (!Utils.isNullOrEmpty(item.fontStyle)) {
                        text.style.fontStyle = item.fontStyle;
                    }
                    text.setAttribute('text-anchor', fallback(item.textAnchor, 'start'));
                    if (!Utils.isNull(item.anchor)) {
                        text.setAttribute('x', item.anchor.x.toString());
                        text.setAttribute('y', item.anchor.y.toString());
                    }
                    else {
                        text.removeAttribute('x');
                        text.removeAttribute('y');
                    }
                }
                else if (isImage(item)) {
                    const img = el;
                    img.setAttribute('href', item.src);
                    img.setAttribute('preserveAspectRatio', 'none');
                    if (item.width > 0) {
                        img.setAttribute('width', '' + item.width);
                    }
                    else {
                        img.removeAttribute('width');
                    }
                    if (item.height > 0) {
                        img.setAttribute('height', '' + item.height);
                    }
                    else {
                        img.removeAttribute('height');
                    }
                    if (!Utils.isNull(item.x)) {
                        img.setAttribute('x', '' + item.x);
                    }
                    else {
                        img.removeAttribute('x');
                    }
                    if (!Utils.isNull(item.y)) {
                        img.setAttribute('y', '' + item.y);
                    }
                    else {
                        img.removeAttribute('y');
                    }
                }
                if (isUiObject(item)) {
                    const t = item.transformMatrix;
                    if (Utils.isNull(t) || Matrix2D.isIdentity(t)) {
                        el.removeAttribute('transform');
                    }
                    else {
                        el.setAttribute('transform', `matrix(${t.a} ${t.b} ${t.c} ${t.d} ${t.e} ${t.f})`);
                    }
                    const opacity = fallback(item.opacity, 1);
                    if (opacity === 1) {
                        el.removeAttribute('opacity');
                    }
                    else {
                        el.setAttribute('opacity', '' + opacity);
                    }
                }
                if (isPresentationObject(item)) {
                    if (Utils.isNullOrEmpty(item.fill)) {
                        el.removeAttribute('fill');
                    }
                    else if (typeof item.fill === 'string') {
                        el.setAttribute('fill', item.fill);
                    }
                    else {
                        const grad = this._ensureGradient(parent, item.fill);
                        el.setAttribute('fill', `url(#${grad.id})`);
                    }
                    if (Utils.isNullOrEmpty(item.stroke)) {
                        el.removeAttribute('stroke');
                    }
                    else {
                        el.setAttribute('stroke', item.stroke);
                    }
                    if (Utils.isNullOrEmpty(item.dashArray)) {
                        el.removeAttribute('stroke-dasharray');
                    }
                    else {
                        el.setAttribute('stroke-dasharray', item.dashArray.join(' '));
                    }
                    if (Utils.isNullOrEmpty(item.lineCap)) {
                        el.removeAttribute('stroke-linecap');
                    }
                    else {
                        el.setAttribute('stroke-linecap', item.lineCap);
                    }
                    if (Utils.isNullOrEmpty(item.lineJoin)) {
                        el.removeAttribute('stroke-linejoin');
                    }
                    else {
                        el.setAttribute('stroke-linejoin', item.lineJoin);
                    }
                    if (Utils.isNullOrEmpty(item.lineWidth)) {
                        el.removeAttribute('stroke-width');
                    }
                    else {
                        el.setAttribute('stroke-width', '' + item.lineWidth);
                    }
                    //let css = '';
                    //if (!Utils.isNullOrEmpty(item.dashArray)) {
                    //    css += `stroke-dasharray: ${item.dashArray.join(',')};`;
                    //}
                    //if (!Utils.isNullOrEmpty(item.lineCap)) {
                    //    css += `stroke-linecap: ${item.lineCap};`;
                    //}
                    //if (!Utils.isNullOrEmpty(item.lineJoin)) {
                    //    css += `stroke-linejoin: ${item.lineJoin};`;
                    //}
                    //el.style.cssText = css;
                    //el.setAttribute('stroke-width', '' + fallback(item.lineWidth, defaults.lineWidth));
                }
                // hit test visibility
                if (item.inert) {
                    el.style.pointerEvents = 'none';
                }
                else {
                    el.style.pointerEvents = '';
                }
                if ((flow || deepRedraw) && this._hasItems(item)) {
                    // recursion
                    this._draw(scene, el, item.childDrawables, true, deepRedraw);
                }
                j++;
            }
        }
        if (flow) {
            // remove exceeding children (except defs)
            for (let k = parent.children.length - 1; k >= j; k--) {
                const el = parent.children.item(k);
                this._disposeSvg(el);
                el.remove();
            }
        }
    }
    _buildSVGElement(item) {
        if (isShape(item) || item instanceof ShapeElement) {
            return document.createElementNS(SVG_NS, 'path');
        }
        if (isText(item) || item instanceof PacemTextElement) {
            return document.createElementNS(SVG_NS, 'text');
        }
        if (isImage(item) || item instanceof PacemImageElement) {
            return document.createElementNS(SVG_NS, 'image');
        }
        return document.createElementNS(SVG_NS, 'g');
    }
    _ensureGradient(parent, gradient) {
        const store = this._gradients;
        if (!store.has(parent)) {
            store.set(parent, new WeakMap());
        }
        const map = store.get(parent);
        // linear or radial?
        const isLinear = isLinearGradient(gradient), isRadial = isRadialGradient(gradient);
        if (map.has(gradient)) {
            const current = map.get(gradient);
            if ((isLinear && current instanceof SVGLinearGradientElement)
                || (isRadial && current instanceof SVGRadialGradientElement)) {
                // remove from DOM
                current.remove();
                // remove from memoizer
                map.delete(gradient);
            }
        }
        if (!map.has(gradient)) {
            // <defs>
            let defsContainer = parent;
            let defs;
            while (Utils.isNull(defs = defsContainer.querySelector(':scope > defs'))) {
                const parent1 = defsContainer.parentElement;
                if (parent1 instanceof SVGElement) {
                    defsContainer = parent1;
                }
                else {
                    break;
                }
            }
            if (Utils.isNull(defs)) {
                throw new Error('Must provide a <defs> element.');
            }
            var gradientSvg = null;
            if (isLinear) {
                // create linear gradient node
                gradientSvg = document.createElementNS(SVG_NS, 'linearGradient');
            }
            else if (isRadialGradient(gradient)) {
                // create radial gradient node
                gradientSvg = document.createElementNS(SVG_NS, 'radialGradient');
            }
            else {
                throw new Error('Unmanaged gradient type.');
            }
            gradientSvg.setAttribute('gradientUnits', 'userSpaceOnUse');
            gradientSvg.setAttribute('id', `grad-${Utils.uniqueCode()}`);
            defs.appendChild(gradientSvg);
            map.set(gradient, gradientSvg);
        }
        const retval = map.get(gradient);
        if (isLinear) {
            // linear
            retval.setAttribute('x1', gradient.start.x.toString());
            retval.setAttribute('y1', gradient.start.y.toString());
            retval.setAttribute('x2', gradient.end.x.toString());
            retval.setAttribute('y2', gradient.end.y.toString());
        }
        else if (isRadial) {
            retval.setAttribute('cx', gradient.center.x.toString());
            retval.setAttribute('cy', gradient.center.y.toString());
            retval.setAttribute('r', gradient.radius.toString());
        }
        // stops
        let j = 0;
        for (let stop of gradient.stops) {
            while (retval.children.length <= j) {
                const stopSvgAdd = document.createElementNS(SVG_NS, 'stop');
                retval.appendChild(stopSvgAdd);
            }
            const stopSvg = retval.children.item(j);
            stopSvg.setAttribute('offset', (stop.offset * 100) + '%');
            stopSvg.setAttribute('stop-color', stop.color);
            if (!Utils.isNull(stop.opacity)) {
                stopSvg.setAttribute('stop-opacity', stop.opacity.toString());
            }
            j++;
        }
        while (retval.children.length > gradient.stops.length) {
            const lastChild = retval.children.item(retval.children.length - 1);
            retval.removeChild(lastChild);
        }
        return retval;
    }
    _ensureMarker(parent, marker) {
        const store = this._markers;
        if (!store.has(parent)) {
            store.set(parent, new WeakMap());
        }
        const map = store.get(parent);
        if (!map.has(marker)) {
            const markerSvg = document.createElementNS(SVG_NS, 'marker');
            map.set(marker, markerSvg);
            const markerPath = document.createElementNS(SVG_NS, 'path');
            markerSvg.appendChild(markerPath);
            markerSvg.setAttribute('id', 'mark' + Utils.uniqueCode());
            let defsContainer = parent;
            let defs;
            while (Utils.isNull(defs = defsContainer.querySelector(':scope > defs'))) {
                const parent1 = defsContainer.parentElement;
                if (parent1 instanceof SVGElement) {
                    defsContainer = parent1;
                }
                else {
                    break;
                }
            }
            if (Utils.isNull(defs)) {
                throw new Error('Must provide a <defs> element.');
            }
            defs.appendChild(markerSvg);
        }
        const markerSvg = map.get(marker), markerPath = markerSvg.firstElementChild;
        // marker attributes
        markerSvg.setAttribute('orient', 'auto-start-reverse');
        if (!Utils.isNullOrEmpty(marker.viewbox)) {
            markerSvg.setAttribute('viewBox', `${marker.viewbox.x} ${marker.viewbox.y} ${marker.viewbox.width} ${marker.viewbox.height}`);
        }
        else {
            markerSvg.removeAttribute('viewBox');
        }
        if (!Utils.isNullOrEmpty(marker.ref)) {
            markerSvg.setAttribute('refX', marker.ref.x.toString());
            markerSvg.setAttribute('refY', marker.ref.y.toString());
        }
        else {
            markerSvg.removeAttribute('refX');
            markerSvg.removeAttribute('refY');
        }
        if (marker.height > 0) {
            markerSvg.setAttribute('markerHeight', marker.height.toString());
        }
        else {
            markerSvg.removeAttribute('markerHeight');
        }
        if (marker.width > 0) {
            markerSvg.setAttribute('markerWidth', marker.width.toString());
        }
        else {
            markerSvg.removeAttribute('markerWidth');
        }
        // path attributes
        markerPath.setAttribute('d', marker.pathData);
        if (!Utils.isNullOrEmpty(marker.fill)) {
            markerPath.setAttribute('fill', marker.fill);
        }
        else {
            markerPath.removeAttribute('fill');
        }
        if (!Utils.isNullOrEmpty(marker.stroke)) {
            markerPath.setAttribute('stroke', marker.stroke);
        }
        else {
            markerPath.removeAttribute('stroke');
        }
        return markerSvg;
    }
};
PacemSvgAdapterElement = __decorate([
    CustomElement({ tagName: P + '-' + TAG_MIDDLE_NAME + '-svg-adapter' })
], PacemSvgAdapterElement);
export { PacemSvgAdapterElement };
